import { fromZonedTime } from "date-fns-tz";

export type DateOnlyString = string & { readonly __dateOnlyStringBrand: unique symbol };
export type UtcInstantString = string & { readonly __utcInstantStringBrand: unique symbol };

export type UtcRange = {
  from: UtcInstantString;
  to: UtcInstantString;
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DEFAULT_TIME_ZONE = "UTC";

const pad2 = (value: number): string => String(value).padStart(2, "0");

type DateOnlyParts = {
  year: number;
  month: number;
  day: number;
};

const parseParts = (value: unknown): DateOnlyParts | null => {
  if (typeof value !== "string") return null;
  const match = DATE_ONLY_PATTERN.exec(value.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

export const isDateOnlyString = (value: unknown): value is DateOnlyString =>
  parseParts(value) !== null;

export const parseDateOnlyStrict = (value: unknown): DateOnlyString => {
  if (!isDateOnlyString(value)) {
    throw new Error(`Invalid date-only value: ${String(value)}`);
  }
  return value.trim() as DateOnlyString;
};

export const formatDateOnlyFromParts = (year: number, month: number, day: number): DateOnlyString => {
  const value = `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(day)}`;
  return parseDateOnlyStrict(value);
};

export const datePickerValueToDateOnly = (date?: Date | null): DateOnlyString | null => {
  if (!date || Number.isNaN(date.getTime())) return null;
  return formatDateOnlyFromParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

export const dateOnlyToDatePickerValue = (value?: string | null): Date | null => {
  if (!value) return null;
  const parts = parseParts(parseDateOnlyStrict(value));
  if (!parts) return null;
  const { year, month, day } = parts;
  return new Date(year, month - 1, day);
};

export const dateOnlyToUtcMidnight = (value: string): Date => {
  const parts = parseParts(parseDateOnlyStrict(value));
  if (!parts) {
    throw new Error(`Invalid date-only value: ${String(value)}`);
  }
  const { year, month, day } = parts;
  return new Date(Date.UTC(year, month - 1, day));
};

export const dbDateToDateOnly = (value: Date | string | null | undefined): DateOnlyString | null => {
  if (!value) return null;
  if (typeof value === "string" && isDateOnlyString(value)) return parseDateOnlyStrict(value);

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return formatDateOnlyFromParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

export const localDateOnlyRangeToUtcRange = ({
  from,
  to,
  timeZone,
}: {
  from: string;
  to: string;
  timeZone?: string | null;
}): UtcRange => ({
  from: fromZonedTime(`${parseDateOnlyStrict(from)}T00:00:00.000`, timeZone || DEFAULT_TIME_ZONE).toISOString() as UtcInstantString,
  to: fromZonedTime(`${parseDateOnlyStrict(to)}T23:59:59.999`, timeZone || DEFAULT_TIME_ZONE).toISOString() as UtcInstantString,
});
